import React, { useState, useEffect } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { fmtMoney } from "@/lib/api";
import { Package, ChevronLeft, ChevronRight } from "lucide-react";
import { LivePhotoSurface } from "@/components/LivePhotoSurface";
import { mediaStill } from "@/lib/livePhotoMedia";

/** Read-only product viewer: photo gallery + details. Used by admin list and customer shop. */
export const ProductDetailDialog = ({ product, open, onOpenChange, showPrice = true }) => {
  const [idx, setIdx] = useState(0);
  useEffect(() => { setIdx(0); }, [product?.id]);
  if (!product) return null;
  const imgs = product.image_data_urls || [];
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="rounded-sm sm:max-w-lg" data-testid="product-detail-dialog">
        <DialogHeader>
          <DialogTitle data-testid="product-detail-name">{product.name}</DialogTitle>
          {showPrice && (
            <DialogDescription data-testid="product-detail-price">
              {fmtMoney(product.price)}{product.taxable ? " + sales tax" : ""}
            </DialogDescription>
          )}
        </DialogHeader>
        {imgs.length > 0 ? (
          <div className="space-y-2">
            <div className="relative">
              <LivePhotoSurface value={imgs[idx]} alt={product.name} className="w-full max-h-80 aspect-[4/3] rounded-sm border border-border bg-secondary/40" imageClassName="object-contain" testId="product-detail-main-image" eagerMotion />
              {imgs.length > 1 && (
                <>
                  <button type="button" aria-label="Previous photo"
                          className="absolute left-2 top-1/2 -translate-y-1/2 bg-foreground/70 hover:bg-foreground text-background rounded-full p-1.5 transition-colors"
                          onClick={() => setIdx((idx - 1 + imgs.length) % imgs.length)}
                          data-testid="product-detail-prev">
                    <ChevronLeft className="w-5 h-5" />
                  </button>
                  <button type="button" aria-label="Next photo"
                          className="absolute right-2 top-1/2 -translate-y-1/2 bg-foreground/70 hover:bg-foreground text-background rounded-full p-1.5 transition-colors"
                          onClick={() => setIdx((idx + 1) % imgs.length)}
                          data-testid="product-detail-next">
                    <ChevronRight className="w-5 h-5" />
                  </button>
                  <span className="absolute bottom-2 right-2 bg-foreground/70 text-background text-[10px] font-mono rounded-sm px-1.5 py-0.5"
                        data-testid="product-detail-counter">
                    {idx + 1} / {imgs.length}
                  </span>
                </>
              )}
            </div>
            {imgs.length > 1 && (
              <div className="flex gap-2 flex-wrap">
                {imgs.map((u, i) => (
                  <button key={`${u.slice(-32)}-${i}`} type="button" onClick={() => setIdx(i)}
                          className={`rounded-sm border-2 ${i === idx ? "border-primary" : "border-transparent"}`}
                          data-testid={`product-detail-thumb-${i}`}>
                    <span className="relative block"><img src={mediaStill(u)} alt={`${product.name} ${i + 1}`} className="w-14 h-14 object-cover rounded-sm" /></span>
                  </button>
                ))}
              </div>
            )}
          </div>
        ) : (
          <div className="border border-dashed border-border rounded-sm p-8 flex flex-col items-center gap-2 text-muted-foreground">
            <Package className="w-6 h-6" />
            <span className="text-sm">No photos yet</span>
          </div>
        )}
        {product.description && (
          <p className="text-sm whitespace-pre-wrap text-muted-foreground" data-testid="product-detail-description">{product.description}</p>
        )}
      </DialogContent>
    </Dialog>
  );
};
